import * as RadioGroup from '@radix-ui/react-radio-group';
import type { ReactNode } from 'react';
import { Fragment } from 'react';
import type { VariantProps } from '#pie/utils/TailwindUtils';
import { tv } from '#pie/utils/TailwindUtils';
import { InfoTooltip } from '../info-tooltip/InfoTooltip';

export type Option = {
  label: string;
  value: string;
  renderInput?: ReactNode;
  tooltip?: ReactNode;
};

export interface InputRadioProps
  extends Omit<RadioGroup.RadioGroupProps, 'onChange' | 'disabled'>,
    VariantProps<typeof inputRadio> {
  options: Option[];
  className?: string;
  onChange?: (value: string) => void;
  ref?: React.Ref<HTMLDivElement>;
}

export const InputRadio = function InputRadio({
  ref,
  options,
  className,
  onChange,
  disabled,
  isError,
  ...props
}: InputRadioProps) {
  const s = inputRadio({ disabled, isError });

  return (
    <RadioGroup.Root
      className={s.base({ className })}
      onValueChange={onChange}
      disabled={disabled}
      ref={ref}
      {...props}
    >
      {options.map((option, idx) => (
        <Fragment key={idx}>
          <label
            htmlFor={`${props.name}-${option.value}`}
            className="flex cursor-pointer items-center gap-2"
          >
            <RadioGroup.Item
              id={`${props.name}-${option.value}`}
              value={option.value}
              className={s.item()}
            >
              <RadioGroup.Indicator className={s.indicator()} />
            </RadioGroup.Item>

            <span className={s.label()}>
              {option.label}{' '}
              {option.tooltip && <InfoTooltip>{option.tooltip}</InfoTooltip>}
            </span>
          </label>
          {option.renderInput}
        </Fragment>
      ))}
    </RadioGroup.Root>
  );
};

const inputRadio = tv({
  base: 'flex flex-col gap-2',
  slots: {
    indicator:
      "after:bg-action-primary-enabled :disabled:opacity-50 relative flex h-full w-full items-center justify-center after:block after:h-2.5 after:w-2.5 after:rounded-[50%] after:content-['']",
    item: 'data-[state=checked]:border-action-primary-enabled h-4 w-4 cursor-pointer rounded-full border border-border-input-enabled bg-white outline-hidden flex-shrink-0',
    label: 'text-content-body cursor-pointer',
  },
  variants: {
    disabled: {
      true: {
        indicator: 'after:bg-surface-subtle-gray',
        item: 'data-[state=checked]:border-border-input-enabled cursor-not-allowed',
        label: 'text-content-disabled cursor-not-allowed',
      },
    },
    isError: {
      true: {
        indicator: 'after:bg-support-error-bold',
        item: 'data-[state=checked]:border-border-input-error bg-surface-error',
        label: 'text-typography-error',
      },
    },
  },
});
